/**
 * TodaysAppointmentsView — presentational (no data hooks).
 *
 * Faithful port of the Flutter Today screen: a 3-pane master–detail worklist
 * (scrips_msp1_pa `today_appointments_screen.dart` `content()` +
 * `appointment_list_widget.dart`):
 *   0. PAGE HEADER (showcase, zone contract Z4): "Today" title + practice picker.
 *   1. LEFT RAIL (340px): a compact search,
 *      a divider, the Upcoming/In-Progress/Completed bucket tabs, a
 *      "Today, {date}" section label, then the WorklistAppointmentCard list.
 *   2. CENTER: "Select an appointment to start" empty state.
 *   3. RIGHT (wide screens only): "Your notes will be generated here as you
 *      chart" empty state.
 * Selecting a card navigates to the encounter route (the detail panes are the
 * pre-selection empty states, matching Flutter's initial worklist view).
 *
 * Composes DS: Select · PatientSearchField · WorklistAppointmentCard (buckets: the app HeaderTabs)
 * · EmptyState. Colours/type map by ROLE to tokens (flutter-style-port) — zero hex.
 */
import type { ReactNode } from 'react';
import { format } from 'date-fns';
import { PracticePicker, TextField, WorklistAppointmentCard, EmptyState, type PracticePickerOption, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { PageHeader } from '@/shared/ui/components/page-header';
import { HeaderTabs } from '@/shared/ui/components/header-tabs';
import type { WorklistCardData } from './lib/to-worklist-card';
import { Search } from 'lucide-react';
import { practiceNow } from '@/shared/lib/practice-time';

export type BucketKey = 'upcoming' | 'in-progress' | 'completed';

export interface TodaysAppointmentsViewProps {
  /** Selectable practices for the practice switcher (label + value + image). */
  practices: PracticePickerOption[];
  selectedPracticeId: string;
  onPracticeChange: (practiceId: string) => void;
  search: string;
  onSearch: (v: string) => void;
  activeBucket: BucketKey;
  onBucketChange: (b: BucketKey) => void;
  counts: { upcoming: number; inProgress: number; completed: number };
  appts: WorklistCardData[];
  loading?: boolean;
  /** A failed load — rendered distinctly from an empty day (review MEDIUM 1). */
  error?: boolean;
  onRetry?: () => void;
  onOpenAppt: (id: string) => void;
  /** "TODAY, {MMMM d}" section label; defaults to today. Injectable for stories/tests. */
  todayLabel?: string;
  /** An extra page-header action before the practice picker (Today's Phone Call, design TodayScreen header). */
  headerAction?: ReactNode;
  /** Hide this page's own practice picker: embedded in /dashboard, the top bar's picker is the only one (same state). */
  hidePracticePicker?: boolean;
}

/**
 * Header tab label with a trailing count (Flutter `tabTitleWidget`): the count in regular weight,
 * secondary ink, beside the label (design log 13 Oct, the Today buckets as header tabs).
 */
function bucketLabel(title: string, count: number): ReactNode {
  return (
    <>
      {title} <span className="header-tab-count">{count}</span>
    </>
  );
}

/** Empty-bucket message per Flutter `getListWidget()`. */
const EMPTY_BY_BUCKET: Record<BucketKey, string> = {
  upcoming: 'No appointments booked',
  'in-progress': 'No appointments in session',
  completed: 'No completed appointments',
};

export function TodaysAppointmentsView({
  practices,
  selectedPracticeId,
  onPracticeChange,
  search,
  onSearch,
  activeBucket,
  onBucketChange,
  counts,
  appts,
  loading,
  error,
  onRetry,
  onOpenAppt,
  todayLabel,
  headerAction,
  hidePracticePicker,
}: TodaysAppointmentsViewProps) {
  const bucketOptions: { label: ReactNode; value: BucketKey }[] = [
    { label: bucketLabel('Upcoming', counts.upcoming), value: 'upcoming' },
    { label: bucketLabel('In session', counts.inProgress), value: 'in-progress' },
    { label: bucketLabel('Completed', counts.completed), value: 'completed' },
  ];

  const dateLabel = todayLabel ?? format(practiceNow(), 'MMMM d');

  return (
    // Showcase: the page header (zone contract Z4) carries the "Today" title and the practice
    // picker; the 3-pane worklist sits under it at 48px sides / 32px top / 48px bottom. No
    // `bg-surface-background` on the root: inside the white workspace frame that would be a grey
    // container (contract D-60).
    <div data-enc-look className="flex h-full min-h-0 flex-col font-sans">
      {/* Notes-pane visibility is a pure CSS media query — NO JS innerWidth/resize
          listener (no perf/leak surface). Threshold is faithful to Flutter, which
          renders the right pane only when `width - 1024 > 360` (i.e. width > 1384px):
          hide at ≤1384, show at ≥1385. */}
      <style>{`@media (max-width: 1384px){.today-notes-pane{display:none !important;}}`}</style>

      <PageHeader
        title="Today"
        // The buckets are the page's tabs (design log 13 Oct: header tabs are underline tabs on
        // the header's bottom line), so they leave the worklist pane for the header.
        tabs={
          <HeaderTabs<BucketKey>
            tabs={bucketOptions.map((o) => ({ id: o.value, label: o.label }))}
            value={activeBucket}
            onChange={onBucketChange}
            aria-label="Appointment buckets"
          />
        }
        actions={
          <>
            {headerAction}
            {/* Practice switcher with the practice avatar (Flutter organisationWidget
                CircleImage + dropdown). No clear ✕ — a practice is always required
                to scope the worklist. */}
            {!hidePracticePicker && (
              <PracticePicker
                options={practices}
                value={selectedPracticeId}
                onChange={onPracticeChange}
                placeholder="Select practice"
              />
            )}
          </>
        }
      />

      <div className="flex min-h-0 flex-1 px-12 pt-8 pb-12">
        {/* ── LEFT RAIL: the worklist ─────────────────────────────────────── */}
        <aside className="w-85 shrink-0 flex flex-col min-h-0 border-r border-r-border-default">
          {/* Header: the appointment search (the title and practice picker moved to the page header). */}
          <div className="px-4 pb-0 flex flex-col gap-4">
            {/* Compact appointment search — 36px, matching Flutter's input_field
                search box (height 36 + leading search glyph). PatientSearchField
                renders a 56px field with no size prop, so use the compact TextField
                atom directly (this search feeds a debounced query, not a patient
                results dropdown). */}
            <TextField
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search appointments"
              size="compact"
              leadingIcon={<Search className="size-5" />}
              aria-label="Search appointments"
            />
          </div>

          {/* Divider under the header (Flutter `horizontalLine()`). */}
          <div className="h-px bg-border-default mt-4" />

          {/* Scrollable list region: date section + cards (the bucket tabs are in the page header). */}
          <div className="flex-1 min-h-0 overflow-auto p-4 flex flex-col gap-4">

            {/* "Today, July 8" section label (Flutter FormView header), sentence case in the meta role: no uppercase eyebrow. */}
            <p className="m-0 text-xs font-normal text-text-secondary">
              Today, {dateLabel}
            </p>

            <div className="flex flex-col gap-2">
              {loading ? (
                <BrandLoading label="Loading today's appointments" />
              ) : error ? (
                // A failed load must not read as "no appointments".
                <EmptyState
                  icon="warning"
                  title="Couldn't load appointments"
                  description="Something went wrong fetching today's schedule. Please retry."
                  {...(onRetry ? { ctaLabel: 'Retry', onCtaClick: onRetry } : {})}
                />
              ) : appts.length === 0 ? (
                // Plain per-bucket message (Flutter `getListWidget()` empty state).
                <div className="min-h-18 flex items-center justify-center text-text-hint text-size-sm">
                  {EMPTY_BY_BUCKET[activeBucket]}
                </div>
              ) : (
                appts.map(({ id, ...card }) => (
                  <WorklistAppointmentCard key={id} data-slot="worklist-card" {...card} onClick={() => onOpenAppt(id)} />
                ))
              )}
            </div>
          </div>
        </aside>

        {/* ── CENTER: pre-selection empty state ───────────────────────────── */}
        <section className="flex-1 min-w-0 flex items-center justify-center p-6">
          <p className="text-text-hint text-size-base text-center">
            Select an appointment to start
          </p>
        </section>

        {/* ── RIGHT (wide only): generated-note placeholder ───────────────── */}
        <section className="today-notes-pane flex-1 min-w-0 flex items-center justify-center p-6 border-l border-l-border-default">
          <p className="text-text-hint text-size-base text-center">
            Your notes will be generated here as you chart
          </p>
        </section>
      </div>
    </div>
  );
}
